import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  class: "engineView-container",
  id: "engineView"
}
const _hoisted_2 = ["src"]

import {ref, onMounted, computed, watch, onBeforeUnmount, onBeforeMount, nextTick} from 'vue'
import { $config, $enum } from '@/global/index'
import { useStore } from 'vuex'
import { addListenerEvent, sendListenerEvent, offListenerEvent } from '@/utils'
import { useRoute } from 'vue-router'
import { SystemMode } from '@/types/system'

/////////////////////////////////////// 数据区
const engineVersionParamName = 'engineVersion';  // 引擎版本参数名
interface SerializableMessage {
    type: string;
    data: Record<string, unknown>;
    engineName: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const engineUrl = ref('');
const engineFrameKey = ref(0);
const engineName = $config.ENGINE_NAME;
const store = useStore();
const iframeRef = ref<HTMLIFrameElement | null>(null);
const route = useRoute();
const useNewEngineUrl = computed(() => {
    return store.state.persistence.useNewEngineUrl === true;
}); // 是否使用新的引擎 URL
const engineConfigReady = computed(() => {
    return store.state.viewData.engineConfigReady === true;
});
const engineVersionValue = computed(() => {
    return store.state.persistence.engineVersion
})
let isSwitchingEngine = false;
let isEngineViewMounted = false;
console.log("engineUrl---", iframeRef);

/////////////////////////////////////// computed和watch
const enterScene = computed(() => {
    return store.state.viewData.enterScene;
})
const stationId = computed(() => {
    return store.state.persistence.stationId;
})
const shouldUseNewEngineUrl = computed(() => {
    const sceneInfo = store.state.persistence.currentUavInfo?.sceneInfo;
    const isPilotAssessment =
        store.state.viewData.systemMode === SystemMode.PILOT_ASSESSMENT;
    const isSimulationCourse =
        store.state.viewData.systemMode === SystemMode.ONLINE_TRAINING;

    if(isPilotAssessment){
        //技能考评
        return useNewEngineUrl.value
        && route.meta.flightType === 'transmit'
        && sceneInfo && sceneInfo.sceneTypeId == 7;
    }
    if(isSimulationCourse){
        //在线训练
        const breadcrumb = store.state.persistence.breadcrumb;
        return useNewEngineUrl.value
        && route.meta.flightType === 'transmit'
        && Array.isArray(breadcrumb)
        && Number(breadcrumb[1]?.id) === 7
        && (breadcrumb[2]?.id == $enum.SceneCategoryEnum.科目训练 || breadcrumb[2]?.id == $enum.SceneCategoryEnum.通关测试 || sceneInfo?.sceneCategoryId == $enum.SceneCategoryEnum.模拟考核);
    }
    return false
})

watch(enterScene, async (newValue) => {
    if (newValue) {
        if (!engineConfigReady.value || !isEngineViewMounted) return;
        // 添加实际的处理逻辑
        console.log('enterScene changed to true');
        const engineReloaded = await setEngineUrl()
        // URL 未变化时复用当前引擎；重新创建 iframe 后由 initializeEngineComplete 触发进场。
        if (!engineReloaded) {
            doEnterScene();
        }
    } else {
        // 添加实际的处理逻辑
        console.log('enterScene changed to false');
        doExitScene();
    }
})

watch(engineConfigReady, (ready) => {
    if (ready && isEngineViewMounted) {
        void setEngineUrl();
    }
})


/////////////////////////////////////// 方法区
async function isUrlAccessible(url: string): Promise<boolean> {
    if (!url) return false;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 1000);
    try {
        await fetch(url, {
            method: 'HEAD',
            mode: 'no-cors',
            cache: 'no-store',
            signal: controller.signal
        });
        return true;
    } catch {
        return false;
    } finally {
        window.clearTimeout(timeoutId);
    }
}

async function setEngineUrl(): Promise<boolean> {
    const targetUseVK2 = shouldUseNewEngineUrl.value;
    let rootUrl = '';
    if (process.env.NODE_ENV == 'production') {
        rootUrl = `${window.location.origin}/webapi`;
    }
    if (process.env.NODE_ENV == 'development') {
        const defaultEngineUrl =process.env.VUE_APP_LOCAL_ENGINE_URL as string
        let isAccessible = await isUrlAccessible(defaultEngineUrl);
        rootUrl = isAccessible? defaultEngineUrl: process.env.VUE_APP_ENGINE_URL as string;
    }
    // 固定引擎参数由本方法写入；从地址栏合并其它参数时去掉已占用键，避免 iframe URL 反复叠加 engineName/pureMode/usePthread
    const merged = new URLSearchParams();
    merged.set('engineName', engineName);
    merged.set('pureMode', 'true');
    // merged.set(
    //     'usePthread',
    //     String(!!(window.crossOriginIsolated && window.location.protocol.includes('https')))
    merged.set('spaceMode','true');
    if (targetUseVK2) {
        merged.set(engineVersionParamName, engineVersionValue.value);
    }
    const reservedKeys = new Set(['engineName', 'pureMode', 'usePthread', engineVersionParamName]);
    const mergeQueryString = (qs: string) => {
        if (!qs) return;
        const trimmed = qs.startsWith('?') ? qs.slice(1) : qs;
        const sp = new URLSearchParams(trimmed);
        sp.forEach((value, key) => {
            if (!reservedKeys.has(key) && !merged.has(key)) {
                merged.set(key, value);
            }
        });
    };
    if (window.location.search) {
        mergeQueryString(window.location.search);
    }
    const hash = window.location.hash;
    const hashQ = hash.indexOf('?');
    if (hashQ >= 0) {
        mergeQueryString(hash.slice(hashQ));
    }
    const currentUseVK2 = isVK2EngineUrl(engineUrl.value);
    if (!engineUrl.value || currentUseVK2 !== targetUseVK2) {
        isSwitchingEngine = true;
        if (engineUrl.value) {
            store.commit('viewData/setEngineInitialized', false);
            store.commit('viewData/setIsLoaded3D', false);
        }
        engineUrl.value = `${rootUrl}/#/?${merged.toString()}`;
        // key 变化会销毁旧 iframe，并使用新的 URL 创建全新的三维实例。
        engineFrameKey.value += 1;
        await nextTick();
        isSwitchingEngine = false;
        console.log('engineUrl---url', engineUrl.value);
        return true;
    }
    return false;
}

function getQueryStr(url:string):string {
    const queryStr = url.split('?')[1];
    return queryStr
}

function isVK2EngineUrl(url: string): boolean {
    const queryStr = getQueryStr(url);
    if (!queryStr) return false;
    const params = new URLSearchParams(queryStr);
    return params.has(engineVersionParamName) && params.get(engineVersionParamName) === engineVersionValue.value;

}

function onReceiveMessage(event: MessageEvent): void {
    if (!event) return;
    // 切换渲染期间以及非当前 iframe 的消息都不参与新引擎状态更新。
    if (isSwitchingEngine || event.source !== iframeRef.value?.contentWindow) return;
    const raw = event.data;
    if (raw == null || typeof raw !== 'object') return;
    const data = raw as Record<string, unknown>;
    if (!data.webApiSource && data.engineName && data.engineName !== engineName) return;
    if (data.webApiSource) {
        onWebApiMessage(event);
    } else {
        sendMessage(raw);
    }
}

// 预先定义消息结构，确保所有消息都符合这个格式
function sendMessage(message: any): void {
    if (!message.engineName||engineName == message.engineName) {
        try {
            // 只序列化data部分，其他字段确保已经是可序列化的
            const safeMessage = {
                type: message.type,
                data: message.data ? JSON.parse(JSON.stringify(message.data)) : {},
                engineName: message.engineName
            };
            iframeRef.value?.contentWindow?.postMessage(safeMessage, "*");
        } catch (error) {
            console.error('Failed to serialize message:', error);
        }
    }
}

function onWebApiMessage(event: MessageEvent): void {
    const d = event.data as Record<string, unknown>;
    if (!d || typeof d !== 'object') return;
    if ((!d.engineName || d.engineName === engineName)) {
        if (d.type == "loadSceneResource") {
            console.log("engineUrl---loadSceneResource", event);
            store.commit('viewData/setIsLoaded3D', true);
        }

        if (d.type == "initializeEngineComplete") {
            console.log("engineUrl---initializeEngineComplete", event);
            if(!store.state.viewData.engineInitialized)
            {
              store.commit('viewData/setEngineInitialized', true);
              if (store.state.viewData.enterScene) {
                doEnterScene();
              }
            }
        }
        // 兜底：进度已到 100% 但未收到 complete（例如中间步骤抛错导致 postMessage 未发）
        // if (d.type == "initializeEngineProgress") {
        //     const progress = (d.data as { progress?: number } | undefined)?.progress;
        //     if (typeof progress === 'number' && progress >= 1 && !store.state.viewData.engineInitialized) {
        //         console.warn('onWebApiMessage: treating progress>=1 as engine ready (fallback)');
        //         store.commit('viewData/setEngineInitialized', true);
        //         if (store.state.viewData.enterScene) {
        //             doEnterScene();
        //         }
        //     }
        // }
    }
}

function doEnterScene(): void {
  if (!store.state.viewData.engineInitialized) {
    return;
  }
  let resourceType = null
  if(store.state.persistence.currentResourceTypeId){
    resourceType = store.state.persistence.currentResourceTypeId
  }else{
    resourceType = store.state.persistence.userTrainSettingInfo.resourceTypeId
  }
  let uavNestId = null, uavSpecificationId = null;
  if(store.state.viewData.flyReplayData.enterPageMode == 2){
    uavNestId = store.state.viewData.flyReplayData.uavNestId;
    uavSpecificationId = store.state.viewData.flyReplayData.uavSpecificationId;
  }else{
    uavNestId = store.state.persistence.currentUavInfo.nestId;
    uavSpecificationId = store.state.persistence.currentUavInfo.uavId;
  }

  const message = {
    type: "initializeEngineData",
    data: {
      stationId: stationId.value, //站所id
      deviceHoverColor: "",
      isUavModule: false,
      hideSceneLoadedMsg: false,
      showSwitchStation: false,
      skyBoxPath: 'Textures/DayNight/MountainSkybox.xml',
      resourceType:resourceType,
      extraLoadData: {
        uavNestId: uavNestId,
        uavSpecificationId: uavSpecificationId,
      },
      specifyModelFiles:null
    },
    engineName: engineName,
  };
  if(store.state.persistence.towerIdentify.sceneInfo){
    const resourceFileList = store.state.persistence.towerIdentify.sceneInfo?.stationList[0]?.resourceFileList
    let fileTypeList = ['Materials','Models','Nodes','Textures','Animations']
    let specifyModelFiles = []
    for(let item of resourceFileList){
        for(let childeItem of item){
            if(fileTypeList.includes(childeItem?.fileName)){
                specifyModelFiles.push(`${childeItem.fileName}.pak`)
            }
        }
    }
    specifyModelFiles.push(`${store.state.persistence.towerIdentify.sceneInfo?.stationList[0]?.modelName}.pak`)
    message.data = {
      ...message.data,
      skyBoxPath:"#2a303a",
      extraLoadData:null,
      specifyModelFiles:specifyModelFiles
    };
  }
    console.log("engineUrl---initializeEngineData", message);
  sendMessage(message);
}

function doExitScene(): void {
  if (!store.state.viewData.engineInitialized) {
    return;
  }
  let message = {
    type: "unLoadAllModel",
    data: {},
    engineName: engineName,
  };
  sendMessage(message);
}



/////////////////////////////////////// 生命周期
onBeforeMount(() => {
    addListenerEvent($config.ENGINE_API, (message) => {
        sendMessage(message);
    })
})

onMounted(() => {
    window.addEventListener("message", onReceiveMessage);
    isEngineViewMounted = true;
    if (engineConfigReady.value) {
        void setEngineUrl();
    }
})

onBeforeUnmount(() => {
    isEngineViewMounted = false;
    window.removeEventListener("message", onReceiveMessage);
    offListenerEvent($config.ENGINE_API);
})


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (engineUrl.value)
      ? (_openBlock(), _createElementBlock("iframe", {
          key: engineFrameKey.value,
          ref_key: "iframeRef",
          ref: iframeRef,
          src: engineUrl.value,
          width: "100%",
          height: "100%",
          scrolling: "none"
        }, null, 8, _hoisted_2))
      : _createCommentVNode("", true)
  ]))
}
}

})